/**
 * ClientPhotoDialog (0142, the humanity upgrade) — manage a client's photo
 * from the record hero. A small dialog (the staff "Add photo" flow is the
 * pattern — src/components/PeopleTab.tsx PersonProfileGrid): a live preview
 * (the photo, or the gold initials circle when none), an Add/Change file
 * picker, and Remove when one is on file.
 *
 * A picked file goes through preparePhoto (src/lib/photoPrep.ts) first — it
 * decodes whatever the browser can read, downscales + re-encodes it to fit
 * the upload cap, and turns anything it can't handle into a plain-English
 * inline message rather than a silent failure; the server re-validates what
 * actually arrives (api/_shared/validators/imageUpload.ts). The upload/remove
 * mutations invalidate the record aggregate + roster list, so the hero,
 * roster avatar, and Face Sheet all refresh from server truth; a local
 * `version` token cache-busts THIS dialog's preview immediately.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Dialog } from '@/components/primitives/Dialog';
import { Button } from '@/components/primitives/Button';
import {
  clientPhotoUrl,
  useUploadClientPhoto,
  useRemoveClientPhoto,
} from '@/lib/clientsApi';
import { preparePhoto, photoUploadErrorMessage } from '@/lib/photoPrep';
import { useSupportContact } from '@/hooks/useSupportContact';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

export function ClientPhotoDialog({
  clientId,
  displayName,
  hasPhoto,
  onClose,
  onChanged,
}: {
  clientId: string;
  displayName: string;
  hasPhoto: boolean;
  onClose: () => void;
  /** Fired after a successful upload/remove so the caller can cache-bust its
   *  own photo <img> (the record hero) — the PeopleTab bumpPhotoVersion
   *  discipline; the private/max-age serve response would otherwise show a
   *  stale replace for up to the cache window. */
  onChanged?: () => void;
}): ReactNode {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const upload = useUploadClientPhoto(tenantSlug);
  const remove = useRemoveClientPhoto(tenantSlug);
  const contact = useSupportContact();

  const fileRef = useRef<HTMLInputElement | null>(null);
  // preparing: resizing the picked file in the browser, before any network
  // call. error/saved carry the inline status line under the buttons.
  const [photoState, setPhotoState] = useState<
    { kind: 'idle' } | { kind: 'preparing' } | { kind: 'error'; message: string } | { kind: 'saved' }
  >({ kind: 'idle' });
  const [imgErrored, setImgErrored] = useState(false);
  // Cache-bust token: bumped after an upload/remove so the preview re-fetches.
  const [version, setVersion] = useState(() => Date.now());
  // Local optimistic knowledge of whether a photo is on file (the parent's
  // hasPhoto refreshes on the aggregate invalidation, but the dialog stays
  // open across the mutation — track it here so the buttons flip immediately).
  const [present, setPresent] = useState(hasPhoto);
  useEffect(() => setPresent(hasPhoto), [hasPhoto]);

  const busy = upload.isPending || remove.isPending || photoState.kind === 'preparing';

  const onPickFile = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setPhotoState({ kind: 'preparing' });
    const prepared = await preparePhoto(file);
    if (!prepared.ok) {
      setPhotoState({ kind: 'error', message: prepared.message });
      return;
    }
    try {
      await upload.mutateAsync({ id: clientId, data: prepared.data, mime: prepared.mime });
      setPresent(true);
      setImgErrored(false);
      setVersion(Date.now());
      onChanged?.();
      setPhotoState({ kind: 'saved' });
    } catch (err) {
      setPhotoState({ kind: 'error', message: photoUploadErrorMessage(err, 'client', contact) });
    }
  };

  const onRemove = async (): Promise<void> => {
    setPhotoState({ kind: 'idle' });
    try {
      await remove.mutateAsync({ id: clientId });
      setPresent(false);
      setImgErrored(false);
      setVersion(Date.now());
      onChanged?.();
    } catch (err) {
      setPhotoState({ kind: 'error', message: photoUploadErrorMessage(err, 'client', contact) });
    }
  };

  const showImg = present && !imgErrored;

  return (
    <Dialog
      title="Client photo"
      size="sm"
      testId="client-photo-dialog"
      onClose={onClose}
      footer={
        <Button variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col items-center gap-4">
        {/* Live preview — the photo when present, else the gold identity
            circle (the hero's DL5 gold-circle motif). */}
        {showImg ? (
          <img
            src={clientPhotoUrl(tenantSlug, clientId, version)}
            alt={`Photo of ${displayName}`}
            data-testid="client-photo-dialog-img"
            className="h-24 w-24 rounded-full object-cover bg-sage-100"
            onError={() => setImgErrored(true)}
          />
        ) : (
          <span
            data-testid="client-photo-dialog-initials"
            className="flex h-24 w-24 items-center justify-center rounded-full bg-warm-400 font-display text-3xl font-semibold text-sage-900"
            aria-hidden="true"
          >
            {initials(displayName) || '?'}
          </span>
        )}

        <input
          ref={fileRef}
          type="file"
          accept="image/*,.heic,.heif"
          className="hidden"
          data-testid="client-photo-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            void onPickFile(file);
          }}
        />

        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            loading={upload.isPending || photoState.kind === 'preparing'}
            data-testid="client-photo-pick"
            onClick={() => fileRef.current?.click()}
          >
            {present ? 'Change photo' : 'Add photo'}
          </Button>
          {present && (
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              loading={remove.isPending}
              data-testid="client-photo-remove"
              onClick={onRemove}
            >
              Remove photo
            </Button>
          )}
        </div>

        <p className="text-center text-xs text-ink-soft">
          Any photo from your phone or computer. It shows on this record, the roster and the Face Sheet.
        </p>

        {photoState.kind === 'preparing' && (
          <p className="text-xs text-ink-soft" data-testid="client-photo-status">
            Preparing photo…
          </p>
        )}
        {photoState.kind === 'saved' && (
          <p className="text-xs text-sage-700" data-testid="client-photo-status">
            Photo saved.
          </p>
        )}
        {photoState.kind === 'error' && (
          <p className="text-xs text-pepper" role="alert" data-testid="client-photo-error">
            {photoState.message}
          </p>
        )}
      </div>
    </Dialog>
  );
}
